<script setup>
import { ref, computed } from 'vue';
import Auth from 'dashboard/api/auth';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useI18n } from 'vue-i18n';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'next/icon/Icon.vue';
import SidebarProfileMenuStatus from './SidebarProfileMenuStatus.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSeparator,
  DropdownItem,
} from 'next/dropdown-menu/base';
import CustomBrandPolicyWrapper from '../../components/CustomBrandPolicyWrapper.vue';

const props = defineProps({
  isCollapsed: { type: Boolean, default: false },
  variant: { type: String, default: 'sidebar' },
});

const emit = defineEmits(['close', 'openKeyShortcutModal']);

defineOptions({
  inheritAttrs: false,
});

const { t } = useI18n();
const { currentAccount } = useAccount();

const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const accountId = useMapGetter('getCurrentAccountId');
const globalConfig = useMapGetter('globalConfig/get');
const userAccounts = useMapGetter('getUserAccounts');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const showAccountList = ref(false);

const hasMultipleAccounts = computed(
  () => userAccounts.value.length > 1
);

const sortedAccounts = computed(() =>
  [...(currentUser.value.accounts || [])].sort((a, b) =>
    a.name.localeCompare(b.name)
  )
);

const onChangeAccount = newId => {
  window.location.href = `/app/accounts/${newId}/dashboard`;
};

const isTopbar = computed(() => props.variant === 'topbar');
const isCompact = computed(() => props.isCollapsed || isTopbar.value);

const showChatSupport = computed(() => {
  return (
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.CONTACT_CHATWOOT_SUPPORT_TEAM
    ) && globalConfig.value.chatwootInboxToken
  );
});

const menuItems = computed(() => {
  return [
    {
      show: showChatSupport.value,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.CONTACT_SUPPORT'),
      icon: 'i-lucide-life-buoy',
      click: () => {
        window.$chatwoot.toggle();
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.KEYBOARD_SHORTCUTS'),
      icon: 'i-lucide-keyboard',
      click: () => {
        emit('openKeyShortcutModal');
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.PROFILE_SETTINGS'),
      icon: 'i-lucide-user-pen',
      link: { name: 'profile_settings_index' },
    },
    {
      show: currentUser.value.type === 'SuperAdmin',
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.SUPER_ADMIN_CONSOLE'),
      icon: 'i-lucide-castle',
      link: '/super_admin',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.LOGOUT'),
      icon: 'i-lucide-power',
      click: Auth.logout,
    },
  ];
});

const allowedMenuItems = computed(() => {
  return menuItems.value.filter(item => item.show);
});
</script>

<template>
  <DropdownContainer
    class="relative min-w-0"
    :class="isCompact ? 'w-auto' : 'w-full'"
    @close="emit('close')"
  >
    <template #trigger="{ toggle, isOpen }">
      <button
        class="flex gap-2 items-center text-left rounded-lg cursor-pointer"
        :class="[
          isTopbar
            ? 'p-0.5 hover:opacity-80 transition-opacity'
            : 'p-1 hover:bg-n-alpha-1',
          { 'bg-n-alpha-1': isOpen && !isTopbar },
          isCompact ? 'justify-center' : 'w-full',
        ]"
        :title="isCompact ? currentUser.available_name : undefined"
        @click="toggle"
      >
        <Avatar
          :size="isTopbar ? 24 : 28"
          :name="currentAccount.name || ''"
          :src="currentAccount.avatar_url || ''"
          class="flex-shrink-0"
          rounded-full
        />
        <span
          v-if="isTopbar"
          class="text-n-slate-6 mx-0.5 text-sm select-none"
        >|</span>
        <Avatar
          :size="isTopbar ? 28 : 32"
          :name="currentUser.available_name"
          :src="currentUser.avatar_url"
          :status="currentUserAvailability"
          class="flex-shrink-0"
          rounded-full
        />
        <div v-if="!isCompact" class="min-w-0">
          <div class="text-sm font-medium leading-4 truncate text-n-slate-12">
            {{ currentUser.available_name }}
          </div>
          <div class="text-xs truncate text-n-slate-11">
            {{ currentUser.email }}
          </div>
        </div>
      </button>
    </template>
    <DropdownBody
      :class="
        isTopbar
          ? 'top-full mt-1.5 ltr:right-0 rtl:left-0 z-50 w-80'
          : 'bottom-12 z-50 mb-2 w-64 sm:w-80 ltr:left-0 rtl:right-0'
      "
    >
      <SidebarProfileMenuStatus />
      <DropdownSeparator />
      <!-- Account Switcher -->
      <li v-if="hasMultipleAccounts" class="list-none">
        <button
          class="flex items-center justify-between w-full px-2 py-1.5 text-sm text-n-slate-12 rounded-lg hover:bg-n-alpha-2 transition-colors cursor-pointer"
          @click.stop="showAccountList = !showAccountList"
        >
          <div class="flex items-center gap-3">
            <Icon icon="i-lucide-repeat" class="size-4 text-n-slate-11" />
            <span>Conta: {{ currentAccount.name }}</span>
          </div>
          <span
            class="i-lucide-chevron-down size-3.5 text-n-slate-10 transition-transform duration-200"
            :class="showAccountList ? 'rotate-180' : ''"
          />
        </button>
        <ul
          v-if="showAccountList"
          class="mt-1 mb-1 ml-7 grid gap-0.5 list-none reset-base"
        >
          <li v-for="account in sortedAccounts" :key="account.id">
            <button
              class="flex items-center justify-between w-full px-2 py-1.5 text-[13px] rounded-lg transition-colors cursor-pointer"
              :class="
                account.id === accountId
                  ? 'bg-n-alpha-2 text-n-slate-12 font-medium'
                  : 'text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12'
              "
              @click="onChangeAccount(account.id)"
            >
              <div class="flex items-center gap-2 truncate">
                <Avatar
                  :size="20"
                  :name="account.name"
                  :src="account.avatar_url || ''"
                  class="flex-shrink-0"
                  rounded-full
                />
                <span class="truncate">{{ account.name }}</span>
              </div>
              <Icon
                v-if="account.id === accountId"
                icon="i-lucide-check"
                class="size-3.5 text-n-teal-11 flex-shrink-0"
              />
            </button>
          </li>
        </ul>
      </li>
      <DropdownSeparator v-if="hasMultipleAccounts" />
      <template v-for="item in allowedMenuItems" :key="item.label">
        <CustomBrandPolicyWrapper
          :show-on-custom-branded-instance="item.showOnCustomBrandedInstance"
        >
          <DropdownItem v-if="item.show" v-bind="item" />
        </CustomBrandPolicyWrapper>
      </template>
    </DropdownBody>
  </DropdownContainer>
</template>
